import { isEqual } from 'lodash';

import { hasEmail, hasExtUserId, isValidEmail } from '../../../utils/inputValidation';
import { type UserFromCSV } from '../components/AdminUsers/types';
import { blankUser } from '../constants/admin-users';
import { type User, type UserRole } from '../constants/user-roles';

export type EmailErrorKeys = 'required-email-or-ext-user-id' | 'invalid-email' | 'duplicate-email' | '';
export type ExtUserIdErrorKeys = 'required-email-or-ext-user-id' | 'duplicate-ext-user-id' | '';
export type NameErrorKeys = 'required-name' | 'invalid-name' | '';
export type RoleErrorKeys = 'required-role' | 'invalid-role' | '';

export const emailValidation = (email?: string, extUserId?: string): EmailErrorKeys => {
    if (!hasEmail(email) && !hasExtUserId(extUserId)) return 'required-email-or-ext-user-id';
    if (email && !isValidEmail(email)) return 'invalid-email';
    return '';
};

export const extUserIdValidation = (extUserId?: string, email?: string): ExtUserIdErrorKeys => {
    if (!hasEmail(email) && !hasExtUserId(extUserId)) return 'required-email-or-ext-user-id';
    return '';
};

export const findDuplicateEmailIndexes = <T extends { email?: string }>(users: T[]) => {
    const seenEmails: string[] = [];
    const indexesOfDuplicates: number[] = [];

    users.forEach((user, index) => {
        if (user.email) {
            if (seenEmails.includes(user.email)) indexesOfDuplicates.push(index);
            else seenEmails.push(user.email);
        }
    });

    return indexesOfDuplicates;
};

export const findDuplicateExtUserIdIndexes = <T extends { ext_user_id?: string }>(users: T[]) => {
    const seenExtUserIds: string[] = [];
    const indexesOfDuplicates: number[] = [];

    users.forEach((user, index) => {
        if (user.ext_user_id) {
            if (seenExtUserIds.includes(user.ext_user_id)) indexesOfDuplicates.push(index);
            else seenExtUserIds.push(user.ext_user_id);
        }
    });

    return indexesOfDuplicates;
};

const hasName = (firstName: string): boolean => firstName.trim().length > 0;

const isValidName = (name: unknown): boolean => typeof name === 'string';

export const nameValidation = (name: string): NameErrorKeys => {
    if (!isValidName(name)) return 'invalid-name';
    if (!hasName(name)) return 'required-name';

    return '';
};

export const roleValidation = (role: UserRole[]): RoleErrorKeys => (role.length === 0 ? 'required-role' : '');

export const validateUsers = (users: User[]) => {
    const newErrorIndexes: number[] = [];
    const indexesOfDuplicateEmails = findDuplicateEmailIndexes(users);
    const indexesOfDuplicateExtUserId = findDuplicateExtUserIdIndexes(users);

    users.forEach((user, index) => {
        if (isEqual(user, blankUser)) return; // ignore blank rows
        if (
            nameValidation(user.name) !== '' ||
            emailValidation(user.email, user.ext_user_id) !== '' ||
            extUserIdValidation(user.ext_user_id, user.email) !== '' ||
            indexesOfDuplicateEmails.includes(index) ||
            indexesOfDuplicateExtUserId.includes(index)
        ) {
            newErrorIndexes.push(index);
        }
    });

    return newErrorIndexes;
};

export const validateParsedUsers = ({ users, maxCsvRows }: { users: UserFromCSV[]; maxCsvRows: number }) => {
    const limitExceeded = users.length > maxCsvRows;

    const newErrorIndexes: number[] = [];
    const indexesOfDuplicateEmails = findDuplicateEmailIndexes(users);
    const indexesOfDuplicateExtUserId = findDuplicateExtUserIdIndexes(users);

    users.forEach((user, index) => {
        if (isEqual(user, blankUser)) return; // ignore blank rows
        if (
            nameValidation(user.name) !== '' ||
            emailValidation(user.email, user.ext_user_id) !== '' ||
            extUserIdValidation(user.ext_user_id, user.email) !== ''
        ) {
            newErrorIndexes.push(index);
        }
    });

    return {
        limitExceeded,
        newErrorIndexes,
        indexesOfDuplicateEmails,
        indexesOfDuplicateExtUserId
    };
};
